import { EnvelopeSimple, Gift, Info, Scan, ShieldCheck } from '@phosphor-icons/react';
import Tippy from '@tippyjs/react';
import { useEffect, useState } from 'react';
import { claims } from '@manifoldxyz/claim-contracts';
import { Constants, Network, StringUtils } from '@manifoldxyz/js-ts-utils';
import { getNetworkLogo } from '@/common/networkUtils';
import { TELEMETRY_EVENT_MAP } from '@/common/telemetry';
import { getExtensionAddress, getURLParameter, InstanceState } from '@/common/util';
import StyledButton from '@/components/StyledButton';
import { useInstanceStatus } from '@/hooks/mainStore';
import { useAnalytics } from '@/hooks/useAnalytics';
import useMainStore from '@/store/mainStore';
import CheckoutPriceDetails from './CheckoutPriceDetails';
import CheckoutToken from './CheckoutToken';

export default function CheckoutFlow({
  onConfirmCryptoCheckout: onStartTransactions,
  onConfirmCardCheckout: onConfirmCardCheckout,
}: {
  onConfirmCryptoCheckout: () => void;
  onConfirmCardCheckout: () => void;
}) {
  const studioInstance = useMainStore((state) => state.studioInstance);
  const numberOfMints = useMainStore((state) => state.numberOfMints);
  const setNumberOfMints = useMainStore((state) => state.setNumberOfMints);
  const redemptionCode = useMainStore((state) => state.redemptionCode);
  const setRedemptionCode = useMainStore((state) => state.setRedemptionCode);
  const mintToAddress = useMainStore((state) => state.mintToAddress);
  const setMintToAddress = useMainStore((state) => state.setMintToAddress);
  const collectorEmail = useMainStore((state) => state.collectorEmail);
  const setCollectorEmail = useMainStore((state) => state.setCollectorEmail);
  const walletAddress = useMainStore((state) => state.walletAddress);
  const analytics = useAnalytics();
  const instanceStatus = useInstanceStatus();

  const [isRedemptionCodeValid, setIsRedemptionCodeValid] = useState<boolean>(false);
  const [isValidMintToAddress, setIsValidMintToAddress] = useState<boolean>(true);
  const [isValidEmail, setIsValidEmail] = useState<boolean>(true);
  const [addressErrorMessage, setAddressErrorMessage] = useState<string>('');
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>('');

  // Check if redemption code is required
  const isRedemptionRequired = studioInstance?.publicData.mintingRestriction === 'RedemptionCodes';

  // Set redemption code from URL parameter on mount
  useEffect(
    () => {
      const urlRedeemCode = getURLParameter('redeemCode');
      if (urlRedeemCode && !redemptionCode) {
        setRedemptionCode(urlRedeemCode);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Force numberOfMints to 1 when redemption is required
  useEffect(() => {
    if (isRedemptionRequired && numberOfMints !== 1) {
      setNumberOfMints(1);
    }
  }, [isRedemptionRequired, numberOfMints, setNumberOfMints]);

  // Validate redemption code when it changes
  useEffect(() => {
    if (isRedemptionRequired) {
      // Simple validation - just check if code is not empty
      // In a real implementation, you might want to validate against an API
      setIsRedemptionCodeValid(redemptionCode.trim().length > 0);
    } else {
      setIsRedemptionCodeValid(true);
    }
  }, [redemptionCode, isRedemptionRequired]);

  // Validate mint-to address when it changes
  useEffect(() => {
    if (mintToAddress) {
      // Basic Ethereum address format validation (0x followed by 40 hex characters)
      const addressRegex = /^0x[a-fA-F0-9]{40}$/;
      const isValidFormat = addressRegex.test(mintToAddress);

      // Check for all zeros after 0x (invalid address)
      const isNotAllZeros = !/^0x0+$/.test(mintToAddress);

      // Make sure it's not the same as the sender's address
      const isNotSameAsWallet = walletAddress
        ? mintToAddress.toLowerCase() !== walletAddress.toLowerCase()
        : true;

      if (!isValidFormat) {
        setIsValidMintToAddress(false);
        setAddressErrorMessage(
          'Please enter a valid Ethereum address (0x followed by 40 characters)',
        );
      } else if (!isNotAllZeros) {
        setIsValidMintToAddress(false);
        setAddressErrorMessage('This address is invalid (all zeros)');
      } else if (!isNotSameAsWallet) {
        setIsValidMintToAddress(false);
        setAddressErrorMessage('You cannot gift to your own wallet address');
      } else {
        setIsValidMintToAddress(true);
        setAddressErrorMessage('');
      }
    } else {
      // Always valid when not using the gift feature
      setIsValidMintToAddress(true);
      setAddressErrorMessage('');

      // Reset mintToAddress when disabling the toggle
      if (mintToAddress) {
        setMintToAddress('');
      }
    }
  }, [mintToAddress, walletAddress, setMintToAddress]);

  // Validate email when it changes
  useEffect(() => {
    if (collectorEmail) {
      // Basic email validation regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValid = emailRegex.test(collectorEmail);

      if (!isValid) {
        setIsValidEmail(false);
        setEmailErrorMessage('Please enter a valid email address');
      } else {
        setIsValidEmail(true);
        setEmailErrorMessage('');
      }
    } else {
      // Always valid when empty (since it's optional)
      setIsValidEmail(true);
      setEmailErrorMessage('');
    }
  }, [collectorEmail]);

  const handleConfirmClick = () => {
    analytics.capture({
      event: TELEMETRY_EVENT_MAP.CHECKOUT_BUTTON_CLICK,
      properties: {
        'checkout-type': 'crypto',
      },
    });
    onStartTransactions();
  };

  const handleBuyWithCardClick = () => {
    analytics.capture({
      event: TELEMETRY_EVENT_MAP.CHECKOUT_BUTTON_CLICK,
      properties: {
        'checkout-type': 'card',
      },
    });
    onConfirmCardCheckout();
  };

  const handleRedemptionCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRedemptionCode(e.target.value);
  };

  const handleMintToAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMintToAddress(e.target.value);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCollectorEmail(e.target.value);
  };

  if (!studioInstance) {
    return null;
  }

  const extension = claims.getExtension(getExtensionAddress(studioInstance));

  const checkoutDisabled =
    (isRedemptionRequired && !isRedemptionCodeValid) ||
    !isValidMintToAddress ||
    !isValidEmail ||
    instanceStatus === InstanceState.SoldOut ||
    instanceStatus === InstanceState.Ended ||
    instanceStatus === InstanceState.Upcoming;

  // @TODO: put this into the enable crossmint stuff instead of here
  // because it's repeated in the CrossmintTransactionsFlow component
  const showCrossMint =
    studioInstance?.publicData.crossmintClientId &&
    // @dev: Crossmint does not support any gated/restricted mints
    studioInstance?.publicData.mintingRestriction !== 'Allowlist' &&
    studioInstance?.publicData.mintingRestriction !== 'RedemptionCodes' &&
    // @dev: Crossmint does not support ERC20 payment flow
    studioInstance?.publicData.mintPrice.erc20 === Constants.NULL_ADDRESS;

  // const showCrossMint = false;

  const mintIsNoLongerAvailable =
    instanceStatus === InstanceState.SoldOut || instanceStatus === InstanceState.Ended;

  return (
    <div className="m-claim--flex m-claim--size-full m-claim--flex-col">
      {/* Fixed Header */}
      <div className="m-claim--mb-4 m-claim--shrink-0 m-claim--text-xl m-claim--font-bold m-claim--text-primary">
        Checkout
      </div>

      {/* Scrollable Content */}
      <div className="m-claim--grow m-claim--overflow-y-scroll m-claim--rounded-lg m-claim--bg-surface-secondary m-claim--px-3 m-claim--pb-3">
        <div className="m-claim--mt-4 m-claim--flex m-claim--flex-col m-claim--gap-4">
          <div className="m-claim--flex m-claim--items-center">
            <div className="m-claim--text-sm m-claim--font-semibold m-claim--text-secondary">
              Minting
            </div>
            {studioInstance?.publicData.network && (
              <div className="m-claim--flex m-claim--items-center m-claim--gap-1 m-claim--rounded-full m-claim--bg-surface-secondary m-claim--px-2 m-claim--py-0.5">
                <div className="m-claim--flex m-claim--size-4 m-claim--items-center m-claim--justify-center m-claim--rounded-full m-claim--bg-surface-secondary m-claim--p-0.5">
                  <img
                    src={getNetworkLogo(studioInstance.publicData.network)}
                    alt={`${StringUtils.capitalizeFirstLetter(Network.getDisplayNameForNetworkId(studioInstance.publicData.network))} network`}
                    className="m-claim--size-full m-claim--object-contain"
                  />
                </div>
                <span className="m-claim--text-xs m-claim--font-medium">
                  on{' '}
                  {StringUtils.capitalizeFirstLetter(
                    Network.getDisplayNameForNetworkId(studioInstance.publicData.network),
                  )}
                </span>
              </div>
            )}

            {isRedemptionRequired && (
              <Tippy
                content="Redemption codes can only be used for a single mint"
                maxWidth={200}
              >
                <span className="m-claim--ml-1 m-claim--flex m-claim--items-center m-claim--text-primary">
                  <Info
                    size={16}
                    weight="bold"
                  />
                </span>
              </Tippy>
            )}
          </div>

          {/* Receiving Token */}
          <CheckoutToken
            image={studioInstance?.publicData.asset?.image ?? ''}
            name={studioInstance?.publicData.asset?.name ?? 'No name'}
            count={numberOfMints.toString()}
            disableQuantitySelector={isRedemptionRequired}
          />

          <hr />

          {/* Redemption Code Section - Only shown when required */}
          {isRedemptionRequired && (
            <div className="m-claim--flex m-claim--flex-col m-claim--gap-1">
              <div className="m-claim--flex m-claim--items-center m-claim--gap-2">
                <div className="m-claim--flex m-claim--items-center m-claim--gap-2 m-claim--text-sm m-claim--font-semibold m-claim--text-secondary">
                  <Scan
                    className="m-claim--size-4 m-claim--text-primary"
                    weight="fill"
                  />
                  <span className="m-claim--text-sm m-claim--font-semibold m-claim--text-primary">
                    Redemption Code Required
                  </span>
                </div>
                <div className="m-claim--flex m-claim--items-center m-claim--justify-center m-claim--rounded-full m-claim--bg-surface-secondary m-claim--px-2 m-claim--py-0.5">
                  <span className="m-claim--text-xs m-claim--font-medium m-claim--text-warning">
                    Required
                  </span>
                </div>
              </div>

              <div className="m-claim--mb-2 m-claim--text-xs m-claim--text-secondary">
                This mint requires a valid redemption code to continue. Each code can only be used
                for a single mint.
              </div>

              <div className="m-claim--relative">
                <input
                  type="text"
                  value={redemptionCode}
                  onChange={handleRedemptionCodeChange}
                  placeholder="Enter redemption code"
                  className="m-claim--w-full m-claim--rounded-md m-claim--border m-claim--border-default m-claim--bg-surface-primary m-claim--px-3 m-claim--py-2 m-claim--text-sm focus:m-claim--outline-none focus:m-claim--ring-1 focus:m-claim--ring-surface-accent"
                  aria-label="Redemption code"
                />
              </div>
            </div>
          )}

          {/* Allowlist explanation section */}
          {studioInstance?.publicData.mintingRestriction === 'Allowlist' && (
            <div className="m-claim--flex m-claim--flex-col m-claim--gap-1">
              <div className="m-claim--flex m-claim--items-center m-claim--gap-3">
                <div className="m-claim--flex m-claim--items-center m-claim--gap-2 m-claim--text-sm m-claim--font-semibold m-claim--text-secondary">
                  <ShieldCheck
                    className="m-claim--size-4 m-claim--text-primary"
                    weight="fill"
                  />
                  <span className="m-claim--text-sm m-claim--font-semibold m-claim--text-primary">
                    Allowlist Only
                  </span>
                </div>
                <div className="m-claim--flex m-claim--items-center m-claim--justify-center m-claim--rounded-full m-claim--bg-surface-accent m-claim--px-2 m-claim--py-0.5">
                  <span className="m-claim--text-xs m-claim--font-medium m-claim--text-warning">
                    Gated
                  </span>
                </div>
              </div>

              <div className="m-claim--mb-2 m-claim--text-xs m-claim--text-secondary">
                This mint is only available to a select list of wallets in an allowlist. You'll find
                out if you're on the list when you connect your wallet on the next screen.
              </div>
            </div>
          )}

          {/**
           * Gift NFT Section
           * Uses mintProxy
           * Cannot be used with Allowlists nor Delegate.cash
           */}
          {extension &&
            claims.hasTrait(extension as claims.ClaimDeployment, claims.Trait.Proxy) &&
            studioInstance.publicData.mintingRestriction !== 'Allowlist' && (
              <div className="m-claim--flex m-claim--flex-col m-claim--gap-1">
                <div className="m-claim--flex m-claim--items-center m-claim--gap-3">
                  <div className="m-claim--flex m-claim--items-center m-claim--gap-2 m-claim--text-sm m-claim--font-semibold m-claim--text-secondary">
                    <Gift
                      className="m-claim--size-4 m-claim--text-primary"
                      weight="fill"
                    />
                    <span className="m-claim--text-sm m-claim--font-semibold m-claim--text-primary">
                      Gift
                    </span>
                  </div>
                  <div className="m-claim--flex m-claim--items-center m-claim--justify-center m-claim--rounded-full m-claim--bg-surface-accent m-claim--px-2 m-claim--py-0.5">
                    <span className="m-claim--text-xs m-claim--font-medium m-claim--text-secondary">
                      Optional
                    </span>
                  </div>
                </div>

                <div className="m-claim--mb-2 m-claim--text-xs m-claim--text-secondary">
                  Send this as a gift directly to someone else's wallet, or another of your wallets.
                </div>

                <div className="m-claim--flex m-claim--items-center m-claim--gap-2">
                  <div className="m-claim--relative m-claim--grow">
                    <input
                      type="text"
                      value={mintToAddress}
                      onChange={handleMintToAddressChange}
                      placeholder="Enter recipient's 0x wallet address"
                      className={`m-claim--w-full m-claim--rounded-md m-claim--border m-claim--border-default m-claim--bg-surface-primary m-claim--px-3 m-claim--py-2 m-claim--text-sm focus:m-claim--outline-none focus:m-claim--ring-1 focus:m-claim--ring-surface-accent ${
                        !isValidMintToAddress && mintToAddress ? 'm-claim--border-[#ef4444]' : ''
                      }`}
                      aria-label="Wallet address to receive NFT"
                    />
                    {!isValidMintToAddress && mintToAddress && (
                      <div
                        className="m-claim--mt-1 m-claim--text-xs"
                        style={{ color: '#ef4444' }}
                      >
                        {addressErrorMessage}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

          {/* Email Section */}
          <div className="m-claim--flex m-claim--flex-col m-claim--gap-1">
            <div className="m-claim--flex m-claim--items-center m-claim--gap-3">
              <div className="m-claim--flex m-claim--items-center m-claim--gap-2 m-claim--text-sm m-claim--font-semibold m-claim--text-secondary">
                <EnvelopeSimple
                  className="m-claim--size-4 m-claim--text-primary"
                  weight="fill"
                />
                <span className="m-claim--text-sm m-claim--font-semibold m-claim--text-primary">
                  Email
                </span>
              </div>
              <div className="m-claim--flex m-claim--items-center m-claim--justify-center m-claim--rounded-full m-claim--bg-surface-accent m-claim--px-2 m-claim--py-0.5">
                <span className="m-claim--text-xs m-claim--font-medium m-claim--text-secondary">
                  Optional
                </span>
              </div>
            </div>

            <div className="m-claim--mb-2 m-claim--text-xs m-claim--text-secondary">
              Get updates from this creator about new drops and updates to existing drops.
            </div>

            <div className="m-claim--flex m-claim--items-center m-claim--gap-2">
              <div className="m-claim--relative m-claim--grow">
                <input
                  type="email"
                  value={collectorEmail}
                  onChange={handleEmailChange}
                  placeholder="Enter your email address"
                  className={`m-claim--w-full m-claim--rounded-md m-claim--border m-claim--border-default m-claim--bg-surface-primary m-claim--px-3 m-claim--py-2 m-claim--text-sm focus:m-claim--outline-none focus:m-claim--ring-1 focus:m-claim--ring-surface-accent ${
                    !isValidEmail && collectorEmail ? 'm-claim--border-[#ef4444]' : ''
                  }`}
                  aria-label="Email address"
                />
                {!isValidEmail && collectorEmail && (
                  <div
                    className="m-claim--mt-1 m-claim--text-xs"
                    style={{ color: '#ef4444' }}
                  >
                    {emailErrorMessage}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="m-claim--mt-4 m-claim--shrink-0 m-claim--overflow-visible m-claim--border-default m-claim--pt-4">
        <CheckoutPriceDetails />
        <div className="m-claim--py-4 m-claim--text-center">
          {showCrossMint && (
            <span className="m-claim--text-sm m-claim--font-semibold m-claim--text-secondary">
              Select Payment Method
            </span>
          )}
          {mintIsNoLongerAvailable && (
            <div className="m-claim--text-center m-claim--text-sm m-claim--font-semibold m-claim--text-warning">
              This mint is no longer available
            </div>
          )}
        </div>

        <div className="m-claim--flex m-claim--flex-row m-claim--gap-4">
          {showCrossMint && (
            <StyledButton
              version="secondary"
              onClick={handleBuyWithCardClick}
              disabled={checkoutDisabled}
            >
              CARD
            </StyledButton>
          )}
          <StyledButton
            version="primary"
            onClick={handleConfirmClick}
            disabled={checkoutDisabled}
          >
            {studioInstance?.publicData.crossmintClientId ? 'CRYPTO' : 'CONTINUE'}
          </StyledButton>
        </div>
      </div>
    </div>
  );
}
